import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../utils/axios";
import { API_ROUTES } from "./routes";
import { showToast } from "../components/ui/toast";

export const useGetBrandAssetLibraryQuery = () => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.GET_TEMPLATE_LIBRARY],
    queryFn: () => api.get(API_ROUTES.GET_TEMPLATE_LIBRARY),
  });

  return { data: data?.data?.data ?? [], isPending, isError, error };
};

// The system half of the library lives under the same route — the two are one
// screen to an admin, so they share a controller and a path.
const SYSTEM_TEMPLATES = `${API_ROUTES.GET_TEMPLATE_LIBRARY}/system`;

// The templates Beleef maintains in code, for the network being viewed.
// Disabled until a network is picked — there is nothing to list without one.
export const useGetSystemTemplatesQuery = (networkId) => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [SYSTEM_TEMPLATES, networkId],
    queryFn: () => api.get(SYSTEM_TEMPLATES, { params: { networkId } }),
    enabled: Boolean(networkId),
  });

  return {
    data: data?.data?.data ?? null,
    isPending: Boolean(networkId) && isPending,
    isError,
    error,
  };
};

// Renders the template to a print-ready PDF and saves it. Rendering runs on
// request rather than being stored, so this is slow by nature — the caller
// shows a pending state per template.
export const useDownloadSystemTemplateSample = () =>
  useMutation({
    mutationFn: async ({ networkId, template }) => {
      const response = await api.get(`${SYSTEM_TEMPLATES}/sample`, {
        params: { networkId, builderId: template.id },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `${template.id}.${template.size}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
    onError: async (error) => {
      // The error body arrives as a Blob because the request asked for one.
      let message = "Failed to render sample";
      try {
        const text = await error?.response?.data?.text?.();
        message = JSON.parse(text)?.message || message;
      } catch {
        message = error?.message || message;
      }
      showToast("error", message);
    },
  });

// Brand assets live on the network and on each group, so a save goes to
// whichever owns the folder being edited.
export const useSaveBrandAssetsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ level, id, brandAssets }) =>
      api.put(
        `${level === "group" ? API_ROUTES.GET_ORGANIZATIONS : API_ROUTES.GET_NETWORKS}/${id}`,
        { brandAssets },
      ),
    onError: (error) => {
      showToast(
        "error",
        error?.response?.data?.message || "Failed to save brand assets",
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [API_ROUTES.GET_TEMPLATE_LIBRARY],
      });
    },
  });
};

// Template drafts Claude wrote from uploaded client artwork, for a developer to
// download. Polled while any are still running.
const CONVERSIONS = `${API_ROUTES.GET_TEMPLATE_LIBRARY}/conversions`;

export const useGetArtworkConversionsQuery = (networkId) => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [CONVERSIONS, networkId],
    queryFn: () => api.get(CONVERSIONS, { params: { networkId } }),
    enabled: Boolean(networkId),
    refetchInterval: (query) =>
      (query.state.data?.data?.data || []).some(
        (conversion) =>
          ["queued", "converting"].includes(conversion.status) &&
          !conversion.stalled,
      )
        ? 15000
        : false,
  });

  return {
    data: data?.data?.data ?? [],
    isPending: Boolean(networkId) && isPending,
    isError,
    error,
  };
};

export const useRetryArtworkConversion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, note }) =>
      api.put(`${CONVERSIONS}/${id}/retry`, { note: note || undefined }),
    onError: (error) => {
      showToast(
        "error",
        error?.response?.data?.message || "Failed to retry conversion",
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [CONVERSIONS] });
    },
  });
};
